// features/home/components/BookFlipOverlay.tsx — the book-open page transition
//
// Mounted once in PageShell (outside HomeScreen's own tree) so it survives
// the route change it ends in: HomeScreen unmounts and BookIndexScreen
// mounts underneath while this stays visible, then fades to reveal it.
// Four phases, driven imperatively (direct DOM writes, not React state) —
// this is a one-shot, non-reactive visual effect running over a route
// change, and the timing precision a rewrite-on-every-frame React version
// would need isn't worth the risk of it; the imperative version here was
// validated frame-by-frame as a standalone mock before porting.
//
//   1. Gather  (~750ms) — every currently-visible book cover slides from
//      its Home grid position to one shared point at screen center; the
//      selected one lands on top, the others just behind and faded.
//      Holds still for GATHER_TO_FLIP_DELAY_MS before the next phase.
//   2. Flip    (~700ms) — the selected book rotates open on a hinge at its
//      left edge (perspective + rotateY + backface-visibility), in place.
//      Holds still for FLIP_TO_ZOOM_DELAY_MS before the next phase.
//   3. Zoom    (~480ms) — the opened book grows from that center point to
//      fill the viewport.
//   4. Reveal  — navigate() to the book's index (invisible under the
//      now-opaque, fullscreen overlay), then fade the overlay out.
//
// Respects prefers-reduced-motion at the call site (HomeScreen skips
// calling this entirely and just navigates) rather than here, since this
// component has exactly one caller today — see bookFlipBus.ts.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerBookFlipHandler, type BookFlipParams } from '../bookFlipBus';
import styles from './BookFlipOverlay.module.css';

const GATHER_MS = 750;
const GATHER_TO_FLIP_DELAY_MS = 250;
const FLIP_MS = 700;
const FLIP_TO_ZOOM_DELAY_MS = 300;
const ZOOM_MS = 480;
const REVEAL_GRACE_MS = 400;
const FADE_MS = 220;
const FLIP_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

// A plain, unscoped global class name (not a CSS Modules token) — see the
// comment on its selector in BookFlipOverlay.module.css for why.
const HOME_SUPPRESSED_CLASS = 'book-flip-home-suppressed';

function centerRect() {
  const width = Math.min(240, window.innerWidth * 0.56);
  const height = width * 1.42;
  return {
    left: (window.innerWidth - width) / 2,
    top: Math.max(24, (window.innerHeight - height) / 2 - 20),
    width,
    height,
  };
}

function applyRect(el: HTMLElement, rect: { left: number; top: number; width: number; height: number }) {
  el.style.left = rect.left + 'px';
  el.style.top = rect.top + 'px';
  el.style.width = rect.width + 'px';
  el.style.height = rect.height + 'px';
}

/**
 * Prepares a `sourceEl.cloneNode(true)` clone to stand in for the real
 * book cover, fixing two things a structural clone gets wrong on its own:
 *
 * 1. `data-book-cover="true"` copies straight over, and that's exactly
 *    what `:global(.book-flip-home-suppressed) [data-book-cover] {
 *    opacity: 0; }` (BookFlipOverlay.module.css) targets to hide the real
 *    Home cards while this transition plays. Left on the clone, that same
 *    rule made every clone invisible for its entire lifetime (confirmed
 *    live: opacity stuck at 0 through gather, flip and zoom) — why the
 *    gather/flip phases appeared to not happen at all. Also drop the
 *    button semantics that no longer apply now this is a decorative
 *    element, not a real navigation target (cloneNode never copies
 *    React's attached listener anyway, but the element is still a real,
 *    focusable `<button>` otherwise).
 * 2. `.cover` (TreeBookCard.module.css) has no background of its own —
 *    the per-template gradient only exists via a `.coverFamily .cover`
 *    -style descendant selector keyed off the ANCESTOR `.book` wrapper.
 *    `sourceEl` here is the button itself, so cloning it alone never
 *    carries that ancestor and every clone silently lost its gradient
 *    fill (confirmed live: computed `backgroundImage` was `none`) — easy
 *    to miss since the frame/emblem/title (styled directly, not
 *    ancestor-dependent) still rendered, visually blending into the
 *    app's own dark backdrop instead of showing as an obviously blank
 *    box. Read the resolved value straight off the still-correctly-styled
 *    live source and bake it into the clone as an explicit inline style
 *    instead of depending on cloned ancestry.
 */
function finalizeCoverClone(sourceEl: HTMLElement, clone: HTMLElement): void {
  clone.removeAttribute('data-book-cover');
  clone.removeAttribute('aria-label');
  clone.setAttribute('tabindex', '-1');
  clone.setAttribute('aria-hidden', 'true');
  const sourceStyle = getComputedStyle(sourceEl);
  clone.style.backgroundImage = sourceStyle.backgroundImage;
  clone.style.backgroundColor = sourceStyle.backgroundColor;
}

/**
 * Clones a real book cover for the gather step. Keeps the source's own
 * classes (TreeBookCard.module.css's frame/emblem/title styling — losing
 * these would leave a blank box) and layers .stackClone's positioning on
 * top, plus finalizeCoverClone()'s fixes for what cloning alone gets
 * wrong (suppressed opacity, missing gradient). Sets every property this
 * function or the gather animation itself needs to control (position,
 * z-index, box-shadow, transition) as inline styles rather than trusting
 * .stackClone's own rules to win the cascade — .cover
 * (TreeBookCard.module.css) and .stackClone (BookFlipOverlay.module.css)
 * are two separate stylesheets, so which one's `position`/`box-shadow`/
 * `transition` declaration wins depends on their relative order in the
 * built bundle, which nothing here should depend on.
 */
function cloneCover(sourceEl: HTMLElement, rect: DOMRect, zIndex: number): HTMLElement {
  const clone = sourceEl.cloneNode(true) as HTMLElement;
  clone.className += ' ' + styles.stackClone;
  finalizeCoverClone(sourceEl, clone);
  clone.style.position = 'fixed';
  clone.style.margin = '0';
  clone.style.zIndex = String(zIndex);
  clone.style.boxShadow = '0 16px 34px rgba(0, 0, 0, 0.5)';
  // Duration is GATHER_MS itself (not the shared --duration-slow token,
  // which other unrelated screens also use) so the visual slide always
  // finishes together with the JS timer that advances to the next phase —
  // no separate value to drift out of sync with it.
  clone.style.transition =
    `top ${GATHER_MS}ms var(--ease-smooth), left ${GATHER_MS}ms var(--ease-smooth), ` +
    `width ${GATHER_MS}ms var(--ease-smooth), height ${GATHER_MS}ms var(--ease-smooth), ` +
    `transform ${GATHER_MS}ms var(--ease-smooth), opacity ${GATHER_MS}ms ease`;
  applyRect(clone, rect);
  return clone;
}

export function BookFlipOverlay() {
  const navigate = useNavigate();
  const stackLayerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const coverRef = useRef<HTMLDivElement>(null);
  const coverFrontRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };

    const runFlip = ({ treeId, sourceEl, otherEls }: BookFlipParams) => {
      const stackLayer = stackLayerRef.current;
      const stage = stageRef.current;
      const cover = coverRef.current;
      const coverFront = coverFrontRef.current;
      if (!stackLayer || !stage || !cover || !coverFront) return;

      clearTimers();
      stackLayer.innerHTML = '';
      coverFront.innerHTML = '';

      const target = centerRect();

      // ── Phase 1: gather ──────────────────────────────────────────────
      const selectedClone = cloneCover(sourceEl, sourceEl.getBoundingClientRect(), 100);
      stackLayer.appendChild(selectedClone);

      const otherClones = otherEls.map((el, i) => {
        const clone = cloneCover(el, el.getBoundingClientRect(), 10 + i);
        stackLayer.appendChild(clone);
        return clone;
      });

      document.body.classList.add(HOME_SUPPRESSED_CLASS);
      void stackLayer.offsetWidth; // commit starting rects before animating

      applyRect(selectedClone, target);
      otherClones.forEach((clone, i) => {
        const offset = (i + 1) * 7;
        clone.style.left = target.left + offset + 'px';
        clone.style.top = target.top + offset + 'px';
        clone.style.width = target.width + 'px';
        clone.style.height = target.height + 'px';
        clone.style.transform = `rotate(${(i % 2 === 0 ? 1 : -1) * (2 + i)}deg)`;
        clone.style.opacity = '0.7';
      });

      const t1 = window.setTimeout(() => {
        // Hold the gathered stack still for a beat before the selected
        // book starts to flip open.
        const tGatherHold = window.setTimeout(() => {
          // ── Phase 2: flip, in place at the gathered center rect ───────
          const frontClone = sourceEl.cloneNode(true) as HTMLElement;
          finalizeCoverClone(sourceEl, frontClone);
          coverFront.appendChild(frontClone);
          // Set position/inset as individual properties, not `cssText =`
          // (a full inline-style replacement) — that would wipe out the
          // backgroundImage/backgroundColor finalizeCoverClone just set.
          frontClone.style.position = 'absolute';
          frontClone.style.inset = '0';

          stage.style.transition = 'none';
          applyRect(stage, target);
          stage.style.visibility = 'visible';
          stage.classList.remove(styles.expanding!);
          cover.style.transition = 'none';
          cover.style.transform = 'rotateY(0deg)';
          void stage.offsetWidth;

          selectedClone.style.visibility = 'hidden'; // hand off to the flip stage
          otherClones.forEach((c) => { c.style.opacity = '0'; });

          requestAnimationFrame(() => {
            // Set explicitly (not '', which falls back to .cover's own
            // CSS transition-duration) so the visual rotation always
            // finishes together with the FLIP_MS timer below — same
            // reasoning as the gather clones' transition above.
            cover.style.transition = `transform ${FLIP_MS}ms ${FLIP_EASE}`;
            cover.style.transform = 'rotateY(-180deg)';

            // Wait for the FLIP_MS hinge rotation to finish before zooming.
            const t2 = window.setTimeout(() => {
              // Hold the opened book still for a beat before it zooms in.
              const tFlipHold = window.setTimeout(() => {
                // ── Phase 3: zoom to fill the viewport ─────────────────
                stage.classList.add(styles.expanding!);
                // Phase 2 set transition:'none' inline (so the stage could
                // snap into the gathered rect without animating) and never
                // cleared it — inline styles always beat class rules, so
                // left as-is this silently cancels .expanding's own
                // transition too and the fullscreen resize below would
                // jump instantly instead of animating. Clear it and force
                // a reflow so the transition re-engages before the rect
                // actually changes.
                stage.style.transition = '';
                void stage.offsetWidth;
                applyRect(stage, { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight });

                // Wait for the ZOOM_MS resize transition to finish before
                // navigating — the overlay is opaque and fullscreen by
                // then, so the route swap underneath is invisible.
                const t3 = window.setTimeout(() => {
                  // ── Phase 4: reveal — navigate, then fade this away ───
                  navigate(`/trees/${treeId}`);
                  const t4 = window.setTimeout(() => {
                    stage.style.transition = '';
                    stage.style.opacity = '0';
                    const t5 = window.setTimeout(() => {
                      stage.style.visibility = 'hidden';
                      stage.style.opacity = '';
                      stackLayer.innerHTML = '';
                      document.body.classList.remove(HOME_SUPPRESSED_CLASS);
                    }, FADE_MS);
                    timers.current.push(t5);
                  }, REVEAL_GRACE_MS);
                  timers.current.push(t4);
                }, ZOOM_MS);
                timers.current.push(t3);
              }, FLIP_TO_ZOOM_DELAY_MS);
              timers.current.push(tFlipHold);
            }, FLIP_MS);
            timers.current.push(t2);
          });
        }, GATHER_TO_FLIP_DELAY_MS);
        timers.current.push(tGatherHold);
      }, GATHER_MS);
      timers.current.push(t1);
    };

    registerBookFlipHandler(runFlip);
    return () => {
      registerBookFlipHandler(null);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <>
      {/* data-book-flip-root bumps CSS specificity so this always wins over
          PageShell's `.shell > * { position: relative; z-index: 1; }` reset
          — both are direct children of .shell, and a bare `.stackLayer`/
          `.flipStage` class selector ties that reset's specificity exactly,
          leaving the winner decided by stylesheet import order (verified via
          a live diagnostic: the reset was winning, silently downgrading this
          overlay from position:fixed/z-index:301 to relative/1, which would
          paint it behind the fixed bottom nav at z-index 200). */}
      <div ref={stackLayerRef} className={styles.stackLayer} data-book-flip-root aria-hidden="true" />
      <div ref={stageRef} className={styles.flipStage} data-book-flip-root aria-hidden="true">
        <div className={styles.book}>
          <div className={styles.rightPage} />
          <div ref={coverRef} className={styles.cover}>
            <div ref={coverFrontRef} className={styles.coverFront} />
            <div className={styles.coverBack}>
              <span className={styles.coverBackLabel}>Opening&hellip;</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
